import React from 'react';
import SegmentedControl from '@amzn/awsui-components-core/segmented-control';
import Select from '@amzn/awsui-components-core/select';

type DataMode = 'live' | 'snapshot';

interface DataModeSelectorProps {
  mode: DataMode;
  onModeChange: (mode: DataMode) => void;
  /** Omit the week props to render the Live/Snapshot toggle alone (no week dropdown). */
  selectedWeek?: string | null;
  availableWeeks?: string[];
  onWeekChange?: (week: string) => void;
}

/**
 * THE page-header control pair, identical on every data page: Week dropdown
 * FIRST, then the Live/Snapshot toggle, always on one line (own nowrap flex —
 * SpaceBetween wraps under pressure, which stacked the pair on the Domain
 * page).
 *
 * The week dropdown renders whenever the CALLER wires it (onWeekChange) — an
 * empty week universe (getWeekNumbers loading or failed) shows it DISABLED
 * with a placeholder, never removes it: the pair must not jump around, and a
 * vanished control reads as a bug rather than a data problem.
 */
const DataModeSelector: React.FC<DataModeSelectorProps> = ({
  mode,
  selectedWeek,
  availableWeeks,
  onModeChange,
  onWeekChange,
}) => {
  const weekOptions = (availableWeeks ?? [])
    .filter((w): w is string => Boolean(w))
    .map((w) => ({ label: w, value: w }));

  return (
    <div className="data-mode-selector">
      {onWeekChange && (
        <div className="data-mode-selector__week">
          <Select
            ariaLabel="Reporting week"
            disabled={weekOptions.length === 0}
            placeholder="Week"
            selectedOption={
              weekOptions.find((o) => o.value === selectedWeek) ??
              weekOptions[0] ??
              null
            }
            onChange={({ detail }) =>
              onWeekChange(detail.selectedOption.value!)
            }
            options={weekOptions}
          />
        </div>
      )}
      <SegmentedControl
        selectedId={mode}
        onChange={({ detail }) => onModeChange(detail.selectedId as DataMode)}
        label="Data source"
        options={[
          { text: 'Live', id: 'live' },
          { text: 'Snapshot', id: 'snapshot' },
        ]}
      />
    </div>
  );
};

export default DataModeSelector;
